import { z } from 'zod';
/* ------------------------------------------------------------------ */
/* Đường dẫn an toàn cho `href`                                        */
/* ------------------------------------------------------------------ */
/**
 * Rà soát bảo mật 16/10/2026, mục T1.
 *
 * `previewUrl` (kho nhạc) và `fileUrl` (hợp đồng Network) là chuỗi do người dùng
 * nhập và được gắn thẳng vào `href` của một thẻ `<a>`. Khai `z.string().max(500)`
 * nên `javascript:fetch('/api/v1/team/ho-so-thue')…` lưu được, rồi người khác
 * bấm "Nghe thử" / "Mở file" là chạy. CSP `script-src 'self'` chặn được
 * `javascript:` URI, nhưng đó là **lớp duy nhất** — nay chặn ngay ở đầu vào.
 */
/** `http://` hoặc `https://` — không nhận `javascript:`, `data:`, `vbscript:`… */
export const urlHttpSchema = z
    .string()
    .trim()
    .max(500)
    .refine((u) => /^https?:\/\//i.test(u), 'Chỉ nhận đường dẫn bắt đầu bằng http:// hoặc https://');
/**
 * Như `urlHttpSchema` nhưng **thêm** đường dẫn nội bộ của app (`/api/v1/...`).
 *
 * Bắt buộc phải nới cho `fileUrl` của hợp đồng: chính app sinh ra giá trị đó
 * (`luuTepHopDong` trả `/api/v1/networks/tep/<tên>`), nên `z.string().url()`
 * thuần sẽ chặn luồng tải file của chính mình. `//host` bị loại vì trình duyệt
 * hiểu đó là đường dẫn **ra ngoài** theo scheme hiện tại.
 */
export const urlHttpHoacNoiBoSchema = z
    .string()
    .trim()
    .max(500)
    .refine((u) => (u.startsWith('/') ? !u.startsWith('//') : /^https?:\/\//i.test(u)), 'Chỉ nhận đường dẫn http(s) hoặc đường dẫn nội bộ bắt đầu bằng một dấu "/"');
/* ------------------------------------------------------------------ */
/* Module                                                              */
/* ------------------------------------------------------------------ */
/** Danh sách id module của toàn app (xem CLAUDE.md). */
export const MODULE_IDS = [
    'core',
    'channels',
    'networks',
    'sync',
    'music',
    'production',
    'schedule',
    'analytics',
    'revenue',
    'compliance',
    'team',
    'dashboard',
];
export const moduleIdSchema = z.enum(MODULE_IDS);
/** Tên module hiển thị cho người dùng, đúng theo tên mục menu trong ảnh thiết kế. */
export const MODULE_LABEL = {
    core: 'Nền tảng',
    dashboard: 'Tổng quan',
    channels: 'Quản lý kênh',
    networks: 'Network (MCN)',
    production: 'Sản xuất nội dung',
    schedule: 'Lịch đăng video',
    music: 'Kho nhạc',
    analytics: 'Phân tích hiệu suất',
    revenue: 'Doanh thu',
    compliance: 'Bản quyền & tuân thủ',
    team: 'Nhân sự & phân quyền',
    sync: 'Kết nối API',
};
/* ------------------------------------------------------------------ */
/* Quyền                                                               */
/* ------------------------------------------------------------------ */
/**
 * Mức quyền, xếp từ thấp tới cao.
 * - `none`     : không thấy module.
 * - `assigned` : xem nhưng chỉ những kênh được giao (bảng ChannelAssignment).
 * - `view`     : xem toàn bộ.
 * - `edit`     : xem và sửa.
 * - `admin`    : toàn quyền, gồm cả thao tác quản trị.
 */
export const PERMISSION_LEVELS = ['none', 'assigned', 'view', 'edit', 'admin'];
export const permissionLevelSchema = z.enum(PERMISSION_LEVELS);
export const PERMISSION_RANK = {
    none: 0,
    assigned: 1,
    view: 2,
    edit: 3,
    admin: 4,
};
/**
 * `assigned` đủ để qua yêu cầu `view` (route tự lọc theo kênh được giao),
 * nên ngưỡng của `view` là rank 1 chứ không phải 2.
 */
export const REQUIRED_RANK = {
    view: PERMISSION_RANK.assigned,
    edit: PERMISSION_RANK.edit,
    admin: PERMISSION_RANK.admin,
};
export function hasPermission(level, required) {
    return PERMISSION_RANK[level] >= REQUIRED_RANK[required];
}
/** Nhãn tiếng Việt của mức quyền, dùng trong ma trận phân quyền. */
export const PERMISSION_LABEL = {
    none: 'Không',
    assigned: 'Kênh được giao',
    view: 'Xem',
    edit: 'Sửa',
    admin: 'Toàn quyền',
};
/**
 * Module **có lọc theo kênh được giao**: route của chúng đọc `ChannelAssignment`
 * rồi chỉ trả dữ liệu của những kênh đó.
 *
 * Mức `assigned` chỉ có nghĩa ở đây. `REQUIRED_RANK.view` bằng rank của
 * `assigned`, nên `assigned` **qua được** `requirePermission(module, 'view')`:
 * đặt nó cho module không lọc kênh là siết quyền trên giấy — quản trị tưởng đã
 * giới hạn, thực tế người đó xem được toàn bộ công ty (rà soát 16/10/2026, mục
 * V4). Vì vậy `updateRolePermissionsInputSchema` **từ chối** `assigned` ở các
 * module ngoài danh sách này.
 *
 * Thêm một module vào đây là một lời hứa: mọi route theo kênh của nó phải lọc
 * (đọc) và chặn (ghi) theo kênh được giao. Test
 * `apps/api/src/__tests__/phanQuyenKenh.test.ts` quét lại lời hứa đó.
 */
export const MODULE_CO_LOC_KENH = [
    'dashboard',
    'channels',
    'networks',
    'sync',
    'music',
    'production',
    'schedule',
    'analytics',
    'revenue',
    'compliance',
];
/**
 * Module **không** lọc theo kênh: `core` và `team` không có khái niệm "kênh của
 * tôi" — người dùng, vai trò, nhật ký, nhóm, lương, hồ sơ thuế đều là dữ liệu
 * nhân sự của cả công ty.
 */
export const MODULE_KHONG_LOC_KENH = MODULE_IDS.filter((m) => !MODULE_CO_LOC_KENH.includes(m));
export function coLocKenh(module) {
    return MODULE_CO_LOC_KENH.includes(module);
}
/* ------------------------------------------------------------------ */
/* Phạm vi dữ liệu của vai trò                                         */
/* ------------------------------------------------------------------ */
/**
 * **Nguồn sự thật của việc lọc theo kênh**, khai thẳng trên từng vai trò.
 *
 * Trước 17/10/2026 cờ này được *suy ra*: "có ít nhất một module ở mức
 * `assigned`". Cờ ngầm đó vỡ theo một cách im lặng — quản trị kéo hết các ô của
 * một vai trò về `view` (một việc trông hoàn toàn vô hại) là người giữ vai trò
 * đó thấy **toàn bộ** dữ liệu công ty, và không màn hình nào nói ra điều đó.
 * Đúng tình huống đã xảy ra trên bản chạy thật với vai trò "Quản lý kênh".
 *
 * Nay phạm vi là một ô chọn riêng, hiện ngay trên ma trận phân quyền.
 */
export const PHAM_VI_DU_LIEU = ['toan_cong_ty', 'kenh_duoc_giao'];
export const phamViDuLieuSchema = z.enum(PHAM_VI_DU_LIEU);
export const PHAM_VI_DU_LIEU_LABEL = {
    toan_cong_ty: 'Toàn công ty',
    kenh_duoc_giao: 'Chỉ kênh được giao',
};
export const PHAM_VI_DU_LIEU_MO_TA = {
    toan_cong_ty: 'Thấy số liệu của mọi kênh, mọi đối tác và mọi tổng hợp toàn công ty.',
    kenh_duoc_giao: 'Chỉ thấy kênh có tên mình trong Phân công; không thấy số tổng toàn công ty.',
};
/**
 * Gộp phạm vi của nhiều vai trò. Lấy mức **rộng nhất** — cùng cách gộp với ma
 * trận quyền (lấy mức cao nhất): giữ một vai trò "Toàn công ty" là thấy cả
 * công ty.
 *
 * **Fail-closed**: danh sách rỗng (chưa có vai trò nào, hoặc không đọc được)
 * trả `kenh_duoc_giao`, chứ không mở toang.
 */
export function gopPhamViDuLieu(danhSach) {
    return danhSach.includes('toan_cong_ty') ? 'toan_cong_ty' : 'kenh_duoc_giao';
}
export const permissionMapSchema = z.record(moduleIdSchema, permissionLevelSchema);
/* ------------------------------------------------------------------ */
/* Vai trò                                                             */
/* ------------------------------------------------------------------ */
export const ROLE_KEYS = [
    'admin',
    'channel_manager',
    'editor',
    'accountant',
    'collaborator',
];
export const roleKeySchema = z.enum(ROLE_KEYS);
export const ROLE_LABEL = {
    admin: 'Quản trị',
    channel_manager: 'Quản lý kênh',
    editor: 'Biên tập / Dựng',
    accountant: 'Kế toán',
    collaborator: 'Cộng tác viên',
};
/* ------------------------------------------------------------------ */
/* Người dùng                                                          */
/* ------------------------------------------------------------------ */
/**
 * - `active`: dùng được.
 * - `invited`: đã mời, chưa đặt mật khẩu.
 * - `disabled`: **khoá tạm**, mở lại bằng một nút.
 * - `archived`: **lưu trữ** (nhân sự đã nghỉ) — mất quyền vĩnh viễn, không hiện
 *   trong danh sách thành viên hay ô chọn người phụ trách, chỉ quay lại bằng
 *   "Khôi phục" và buộc đặt lại mật khẩu.
 */
export const USER_STATUSES = ['active', 'invited', 'disabled', 'archived'];
export const userStatusSchema = z.enum(USER_STATUSES);
export const USER_STATUS_LABEL = {
    active: 'Đang hoạt động',
    invited: 'Đã mời',
    disabled: 'Đã khoá',
    archived: 'Đã lưu trữ',
};
export const userSummarySchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string(),
    avatarColor: z.string(),
    status: userStatusSchema,
    twoFactorEnabled: z.boolean(),
    lastActiveAt: z.iso.datetime().nullable(),
    roles: z.array(roleKeySchema),
});
/* ------------------------------------------------------------------ */
/* Xác thực                                                            */
/* ------------------------------------------------------------------ */
export const loginInputSchema = z.object({
    email: z.email('Email không hợp lệ'),
    password: z.string().min(1, 'Chưa nhập mật khẩu'),
});
/** Mật khẩu tối thiểu, dùng cho mọi chỗ đặt mật khẩu trong app. */
export const DAI_MAT_KHAU_TOI_THIEU = 12;
export const matKhauMoiSchema = z
    .string()
    .min(DAI_MAT_KHAU_TOI_THIEU, `Mật khẩu tối thiểu ${String(DAI_MAT_KHAU_TOI_THIEU)} ký tự`)
    .max(200, 'Mật khẩu quá dài');
/**
 * Đổi mật khẩu của chính mình. Phải nhập mật khẩu hiện tại: cookie phiên bị
 * đánh cắp thì vẫn không đổi được mật khẩu.
 */
export const doiMatKhauInputSchema = z
    .object({
    currentPassword: z.string().min(1, 'Chưa nhập mật khẩu hiện tại'),
    newPassword: matKhauMoiSchema,
    confirmPassword: z.string().min(1, 'Chưa nhập lại mật khẩu mới'),
})
    .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Hai lần nhập mật khẩu mới không khớp',
    path: ['confirmPassword'],
})
    .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'Mật khẩu mới phải khác mật khẩu hiện tại',
    path: ['newPassword'],
});
/** Email đăng nhập, luôn so sánh và lưu ở dạng chữ thường. */
export const emailDangNhapSchema = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('Email không hợp lệ'));
/** Đổi email của chính mình: nhập email mới hai lần và xác nhận bằng mật khẩu. */
export const doiEmailInputSchema = z
    .object({
    currentPassword: z.string().min(1, 'Chưa nhập mật khẩu hiện tại'),
    email: emailDangNhapSchema,
    confirmEmail: emailDangNhapSchema,
})
    .refine((d) => d.email === d.confirmEmail, {
    message: 'Hai lần nhập email không khớp',
    path: ['confirmEmail'],
});
/** Chế độ dữ liệu YouTube của máy chủ (`YOUTUBE_MODE`). */
export const youtubeModeSchema = z.enum(['mock', 'live']);
/**
 * App đang chạy ở đâu (`APP_ENV` của API).
 *
 * `local` (mặc định): máy dev Windows — `npm run dev` và `npm run dev:live`.
 * `production`: máy chủ thật, do `docker-compose.prod.yml` đặt sẵn.
 *
 * Tách khỏi `NODE_ENV` vì `NODE_ENV=production` còn xuất hiện ở những lần chạy
 * bản build trên máy dev; giá trị này chỉ nói về **máy chủ thật**.
 */
export const appEnvSchema = z.enum(['local', 'production']);
export const meResponseSchema = z.object({
    user: userSummarySchema,
    permissions: permissionMapSchema,
    /** `null` = không bị giới hạn kênh. Mảng = chỉ được xem các kênh này. */
    assignedChannelIds: z.array(z.string()).nullable(),
    /**
     * Phạm vi dữ liệu đã gộp từ mọi vai trò của người này.
     *
     * Giao diện cần nó để phân biệt hai trạng thái rỗng trông giống hệt nhau:
     * "công ty chưa có kênh nào" (quản trị → mời thêm kênh) và "bạn chưa được
     * giao kênh nào" (nhân sự → hướng dẫn tự thêm kênh / nhờ quản trị giao).
     *
     * `.default('kenh_duoc_giao')` để tab đang mở với payload cũ không vỡ, và vì
     * đoán sai về phía **giới hạn** thì chỉ hiện thừa lời hướng dẫn, đoán sai về
     * phía ngược lại thì giấu mất lời hướng dẫn đúng.
     */
    phamViDuLieu: phamViDuLieuSchema.default('kenh_duoc_giao'),
    /**
     * Người này **đang bị ép** bật xác thực hai bước ngay bây giờ.
     *
     * Gộp cả ba điều kiện: thuộc nhóm bắt buộc (`canXacThucHaiBuoc`), chưa bật,
     * **và** môi trường đang ép (`lib/epHaiBuoc.ts`). Giao diện chỉ cần đọc một
     * cờ — nó không phải biết luật, và không có bản sao luật thứ hai để lệch.
     *
     * `.default(false)` để tab đang mở với payload cũ không vỡ, và vì đoán sai
     * về phía *không ép* chỉ chậm một nhịp (hook máy chủ chặn lại ngay), còn đoán
     * sai phía kia là khoá người dùng ra khỏi app.
     */
    buocBat2fa: z.boolean().default(false),
    /**
     * Chế độ dữ liệu YouTube của máy chủ (`YOUTUBE_MODE`).
     *
     * Giao diện cần biết ở **mọi trang** để chỉ gắn nhãn "Dữ liệu mẫu" khi đang
     * chạy mock. `GET /api/v1/sync/summary` cũng trả `mode`, nhưng route đó cần
     * quyền `sync:view` mà vai trò Cộng tác viên không có.
     */
    youtubeMode: youtubeModeSchema,
    /**
     * Tên database đang dùng (`qlk_main`, `qlk_live_thu`…), lấy từ phần path của
     * `DATABASE_URL`. Chỉ tên database — **không** user, host hay mật khẩu.
     *
     * Dải nhãn trên cùng mọi trang in tên này để không ai nhầm giữa dữ liệu thử
     * và dữ liệu thật.
     */
    databaseName: z.string(),
    /**
     * Máy chủ thật hay máy dev (`APP_ENV`). Giao diện dùng để **ẩn dải đỏ "DỮ
     * LIỆU THẬT"** trên bản chạy thật: ở đó mọi người chỉ có một database duy
     * nhất nên dải này không phân biệt được gì, chỉ chiếm chỗ. Trên máy dev thì
     * hai database trông giống nhau từng pixel nên dải vẫn phải còn.
     *
     * `.default('local')` để tab đang mở với payload cũ (chưa có trường này)
     * không vỡ: thiếu thì coi như máy dev, tức là vẫn hiện dải.
     */
    appEnv: appEnvSchema.default('local'),
});
/* ------------------------------------------------------------------ */
/* Lỗi và phân trang                                                   */
/* ------------------------------------------------------------------ */
export const API_ERROR_CODES = [
    'VALIDATION_ERROR',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'RATE_LIMITED',
    /**
     * Bắt buộc bật xác thực hai bước mà chưa bật (rà soát mục C3, đợt 8b). Trả
     * **403**, và là một mã riêng chứ không gộp vào `FORBIDDEN`: giao diện phải
     * phân biệt được "không đủ quyền" (hiện trang 403) với "đi bật 2FA đã"
     * (chuyển sang màn ép bật).
     */
    'CAN_2FA',
    'INTERNAL',
];
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.enum(API_ERROR_CODES),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
/** Tham số phân trang theo cursor, dùng chung cho mọi danh sách. */
export const cursorQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
/* ------------------------------------------------------------------ */
/* Khác                                                                */
/* ------------------------------------------------------------------ */
/** Múi giờ hiển thị của app. Dữ liệu luôn lưu UTC. */
export const APP_TIMEZONE = 'Asia/Ho_Chi_Minh';
/** Màu avatar dùng khi tạo người dùng mới. */
export const AVATAR_COLORS = [
    '#4F46E5',
    '#B42318',
    '#047857',
    '#1D4ED8',
    '#C11574',
    '#B54708',
    '#6938EF',
    '#026AA2',
];
//# sourceMappingURL=core.js.map